import React, { useState, useEffect, useRef } from 'react';
import { reelDiscovery } from '../api';

export default function TrendingSounds({ onSelect, compact }) {
  const [sounds, setSounds] = useState([]);
  const [moods, setMoods] = useState([]);
  const [activeMood, setActiveMood] = useState('');
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    reelDiscovery.getTrendingSounds()
      .then(data => {
        setSounds(data.sounds || []);
        setMoods(data.moods || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const togglePreview = (e, sound) => {
    e.stopPropagation();
    if (playingId === sound.id) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setPlayingId(null);
      return;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    const url = sound.url || `https://www.soundhelix.com/examples/mp3/SoundHelix-Song-${(sound.id % 16) + 1}.mp3`;
    const audio = new Audio(url);
    audio.volume = 0.5;
    audio.loop = false;
    audio.onended = () => setPlayingId(null);
    audio.onerror = () => {
      setPlayingId(null);
    };
    audio.play().catch(() => setPlayingId(null));
    audioRef.current = audio;
    setPlayingId(sound.id);
  };

  const filtered = activeMood ? sounds.filter(s => s.mood === activeMood) : sounds;
  const topSounds = filtered.slice(0, compact ? 10 : 20);

  if (loading) {
    return <div style={{ padding: 16, color: 'var(--reels-muted, #666)' }}>Loading trending sounds...</div>;
  }

  return (
    <div style={{ padding: compact ? '4px 0' : '12px 0' }}>
      {!compact && (
        <h4 style={{ color: '#f5a623', marginBottom: 12, fontSize: 14, fontWeight: 700 }}>
          🎵 Trending Sounds
        </h4>
      )}

      {moods.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: compact ? 8 : 12 }}>
          <button
            onClick={() => setActiveMood('')}
            style={{
              padding: '4px 12px',
              borderRadius: 20,
              border: '1px solid var(--reels-border, #2a3a48)',
              background: !activeMood ? 'rgba(78,203,141,0.15)' : 'transparent',
              color: !activeMood ? '#4ecb8d' : '#999',
              cursor: 'pointer',
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            All
          </button>
          {moods.map((mood) => (
            <button
              key={mood}
              onClick={() => setActiveMood(mood)}
              style={{
                padding: '4px 12px',
                borderRadius: 20,
                border: '1px solid var(--reels-border, #2a3a48)',
                background: activeMood === mood ? 'rgba(78,203,141,0.15)' : 'transparent',
                color: activeMood === mood ? '#4ecb8d' : '#999',
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              {mood}
            </button>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8 }}>
        {topSounds.map((sound) => {
          const isPlaying = playingId === sound.id;
          return (
            <button
              key={sound.id}
              onClick={() => onSelect && onSelect(sound)}
              style={{
                minWidth: compact ? 100 : 120,
                padding: compact ? '8px' : '12px',
                borderRadius: 8,
                border: isPlaying ? '1px solid #4ecb8d' : '1px solid var(--reels-border, #2a3a48)',
                background: isPlaying ? 'rgba(78,203,141,0.12)' : 'var(--reels-card-bg, rgba(255,255,255,0.05))',
                cursor: 'pointer',
                textAlign: 'center',
                flexShrink: 0,
                position: 'relative',
                transition: 'all 0.2s',
              }}
            >
              <div
                onClick={(e) => togglePreview(e, sound)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: isPlaying ? '#4ecb8d' : 'rgba(255,255,255,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 6px',
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                {isPlaying ? '⏹' : '▶️'}
              </div>
              <div style={{ fontSize: compact ? 10 : 11, fontWeight: 700, color: 'var(--reels-fg, #fff)', marginBottom: 2 }}>
                {sound.name}
              </div>
              {sound.artist && (
                <div style={{ fontSize: compact ? 9 : 10, color: 'var(--reels-muted, #999)' }}>{sound.artist}</div>
              )}
              <div style={{ fontSize: 10, color: '#4ecb8d', marginTop: 4 }}>
                {sound.usageCount || 0} uses
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
